import { useParticipant, useMeeting } from '@videosdk.live/react-sdk'
import MicOffIcon from '@/icons/ParticipantTabPanel/MicOffIcon'
import MicOnIcon from '@/icons/ParticipantTabPanel/MicOnIcon'
import RaiseHand from '@/icons/ParticipantTabPanel/RaiseHand'
import VideoCamOffIcon from '@/icons/ParticipantTabPanel/VideoCamOffIcon'
import VideoCamOnIcon from '@/icons/ParticipantTabPanel/VideoCamOnIcon'
import { nameTruncated } from '@/utils/helper'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { SpeakingRole } from '@db8/shared'
import { UserPlus, UserRoundMinus } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import useDarkMode from '@/hooks/useDarkMode'
import { useMemo, useState } from 'react'
import ProfileImage from '@/components/ProfileImage'

export const ParticipantListItem = ({
  participantId,
  raisedHand,
  user,
  dragRegionListeners,
}: {
  participantId: string
  raisedHand: boolean
  user: any
  dragRegionListeners?: any
}) => {
  const { micOn, webcamOn, isLocal } = useParticipant(participantId)
  const {
    enableMic: pEnableMic,
    disableMic: pDisableMic,
    enableWebcam: pEnableWebcam,
    disableWebcam: pDisableWebcam,
    remove,
  } = useParticipant(participantId)
  const meeting = useMeeting()

  const { isModerator, addBot2, isBot, botUsers, removeUserById } = useHallUsers()
  const isDark = useDarkMode()
  const canControl = isLocal || isModerator

  const toggleMic = () => {
    if (!canControl) return
    try {
      if (isLocal) {
        const api: any = meeting
        if (typeof api?.toggleMic === 'function') api.toggleMic()
        else if (micOn && typeof api?.muteMic === 'function') api.muteMic()
        else if (!micOn && typeof api?.unmuteMic === 'function') api.unmuteMic()
        else micOn ? pDisableMic() : pEnableMic()
      } else {
        const api: any = meeting
        if (micOn) {
          if (typeof api?.muteMic === 'function') api.muteMic(participantId)
          else pDisableMic()
        } else {
          if (typeof api?.unmuteMic === 'function') api.unmuteMic(participantId)
          else pEnableMic()
        }
      }
    } catch {}
  }

  const toggleWebcam = () => {
    if (!canControl) return
    try {
      if (isLocal) {
        const api: any = meeting
        if (typeof api?.toggleWebcam === 'function') api.toggleWebcam()
        else if (webcamOn && typeof api?.disableWebcam === 'function') api.disableWebcam()
        else if (!webcamOn && typeof api?.enableWebcam === 'function') api.enableWebcam()
        else webcamOn ? pDisableWebcam() : pEnableWebcam()
      } else {
        const api: any = meeting
        if (webcamOn) {
          if (typeof api?.disableWebcam === 'function') api.disableWebcam(participantId)
          else pDisableWebcam()
        } else {
          if (typeof api?.enableWebcam === 'function') api.enableWebcam(participantId)
          else pEnableWebcam()
        }
      }
    } catch {}
  }

  const removeUser = (u: any) => {
    if (!isModerator) return
    if (u.botId) removeUserById(u.id)
    else {
      try {
        remove()
      } catch {}
    }
  }

  const isSelected = `bg-opacity-40 text-white`
  const getBgColor = () => {
    if (user.speakingRole === SpeakingRole.Affirmative) return `bg-green-500 ${isSelected}`
    if (user.speakingRole === SpeakingRole.Negative) return `bg-blue-500 ${isSelected}`
    if (user.speakingRole === SpeakingRole.Moderator) return `bg-purple-500 ${isSelected}`
    return 'bg-gray-200 border border-gray-400 dark:border-white dark:bg-transparent text-gray-900 dark:text-white'
  }

  const userIsSpectator = useMemo(
    () => user.speakingRole === SpeakingRole.None,
    [user.speakingRole],
  )
  const iconColor = useMemo(
    () => (useDarkMode ? 'white' : userIsSpectator ? '#9ca3af' : '#6b7280'),
    [useDarkMode, userIsSpectator],
  )

  const btnStyles = `p-1 btn-group-item w-[35px] bg-transparent hover:bg-transparent hover:opacity-70 border-faint !shadow-none ${
    !userIsSpectator ? 'bg-gray-50 bg-opacity-90 hover:bg-gray-100 dark:bg-transparent ' : ''
  }`

  const removable = useMemo(() => {
    if (!isModerator) return false
    if (!isBot(user)) return true
    return isBot(user) && botUsers.length > 1
  }, [isModerator, user, botUsers, isBot])

  const [addingBot, setAddingBot] = useState(false)
  const onAddBot = async () => {
    try {
      setAddingBot(true)
      await addBot2()
    } finally {
      setAddingBot(false)
    }
  }

  return (
    <div
      className={`p-1 rounded-md overflow-hidden border !max-h-[50px] flex-grow ${getBgColor()}`}
    >
      <div className="flex flex-1 items-center justify-center relative">
        <div className="flex items-center flex-1 min-w-0" {...(dragRegionListeners || {})}>
          <div
            className="h-10 w-10 text-base rounded overflow-hidden flex items-center justify-center bg-gray-300 dark:bg-gray-500 dark:bg-gray-900 text-white cursor-grab active:cursor-grabbing"
            aria-label="Drag participant"
          >
            <ProfileImage
              user={user}
              className="h-full w-full text-xl flex items-center justify-center"
            />
          </div>
          <div className="ml-2 mr-2 flex flex-1 min-w-0">
            <p className="text-sm font-semibold overflow-hidden whitespace-pre-wrap overflow-ellipsis">
              {isLocal ? 'You' : nameTruncated(user.displayName, 15)}
            </p>
          </div>
        </div>

        {raisedHand && (
          <div className="flex items-center justify-center m-1 p-1">
            <RaiseHand fillcolor="#fff" />
          </div>
        )}

        <div
          className="btn-group inline-flex"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {!isBot(user) && (
            <Button
              variant="outline"
              className={`${btnStyles} ${canControl ? 'cursor-pointer' : ''}`}
              onClick={toggleMic}
              disabled={!canControl}
              size="sm"
            >
              {micOn ? <MicOnIcon color={iconColor} /> : <MicOffIcon color={iconColor} />}
            </Button>
          )}

          {!isBot(user) && (
            <Button
              variant="outline"
              className={`${btnStyles} ${canControl ? 'cursor-pointer' : ''}`}
              onClick={toggleWebcam}
              disabled={!canControl}
              size="sm"
            >
              {webcamOn ? (
                <VideoCamOnIcon color={iconColor} />
              ) : (
                <VideoCamOffIcon color={iconColor} />
              )}
            </Button>
          )}

          {removable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`${btnStyles} text-red-500 cursor-pointer hover:text-red-400`}
                  onClick={() => removeUser(user)}
                >
                  <UserRoundMinus size="20px" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="rounded-md p-2 bg-black">
                <p className="text-xs">Remove user from debate hall</p>
              </TooltipContent>
            </Tooltip>
          )}

          {isModerator && isBot(user) && botUsers.length === 1 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`${btnStyles} text-blue-500 cursor-pointer hover:text-blue-400`}
                  onClick={onAddBot}
                  disabled={addingBot}
                >
                  {addingBot ? <span className="text-xs">Adding…</span> : <UserPlus size="20px" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="rounded-md p-2 bg-black">
                <p className="text-xs">Add Another Bot User</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

export function DraggableParticipantListItem({
  participantId,
  raisedHand,
  user,
}: {
  participantId: string
  raisedHand: boolean
  user: any
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: participantId })
  const style = { transform: CSS.Translate.toString(transform) }
  return (
    <div className="flex w-full items-center" ref={setNodeRef} style={style} {...attributes}>
      <ParticipantListItem
        participantId={user.videoSDKId || ''}
        raisedHand={raisedHand}
        user={user}
        dragRegionListeners={listeners}
      />
    </div>
  )
}
